import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { Form, Button, Row, Col, Card } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import Chart from 'chart.js/auto';
import './LogDashboard.css'; // Import new CSS file

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// Custom Input for DatePicker to support floating labels
const CustomDatePickerInput = forwardRef(({ value, onClick, placeholder }, ref) => (
    <Form.Control
        type="text"
        onClick={onClick}
        value={value}
        ref={ref}
        placeholder={placeholder}
        readOnly
    />
));

const LogDashboard = () => {
    const [logs, setLogs] = useState([]);
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 6); // Last 7 days including today
        return d;
    });
    const [endDate, setEndDate] = useState(new Date());
    const [groupBy, setGroupBy] = useState('hour');
    const [selectedProfileId, setSelectedProfileId] = useState('');
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedActivityType, setSelectedActivityType] = useState('');
    const [allProfiles, setAllProfiles] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [allActivityTypes, setAllActivityTypes] = useState([]);
    const chartInstances = useRef([]);

    useEffect(() => {
        const fetchFilterOptions = async () => {
            try {
                const token = localStorage.getItem('token');
                const [profilesRes, usersRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/profiles`, { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get(`${API_BASE_URL}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
                ]);
                setAllProfiles(profilesRes.data);
                setAllUsers(usersRes.data);

                const recentLogsRes = await axios.get(`${API_BASE_URL}/api/admin/activity/recent?limit=100`, { headers: { Authorization: `Bearer ${token}` } });
                const uniqueActivityTypes = [...new Set(recentLogsRes.data.map(log => log.activity_type))];
                setAllActivityTypes(uniqueActivityTypes);

            } catch (error) {
                console.error('Error fetching filter options:', error);
            }
        };
        fetchFilterOptions();
    }, []);

    const fetchLogs = async () => {
        try {
            const token = localStorage.getItem('token');
            const params = {
                start_date: startDate ? startDate.toISOString() : null,
                end_date: endDate ? endDate.toISOString() : null,
                group_by: groupBy,
                profile_id: selectedProfileId || null,
                user_id: selectedUserId || null,
                activity_type: selectedActivityType || null,
            };
            const response = await axios.get(`${API_BASE_URL}/api/admin/activity/logs`, {
                params,
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setLogs(response.data);
        } catch (error) {
            console.error('Error fetching activity logs:', error);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [startDate, endDate, groupBy, selectedProfileId, selectedUserId, selectedActivityType]);

    useEffect(() => {
        chartInstances.current.forEach(chart => chart.destroy());
        chartInstances.current = [];

        if (logs.length > 0 && allActivityTypes.length > 0) {
            const labels = logs.map(log => log.time_period);

            allActivityTypes.forEach(type => {
                const chartId = `chart-${type}`;
                const ctx = document.getElementById(chartId);

                if (ctx) {
                    const data = logs.map(log => log[type] || 0);
                    // Use colors from the custom palette
                    const colorPalette = ['#4a90e2', '#50e3c2', '#f5a623', '#e74c3c', '#9b59b6', '#34495e'];
                    const color = colorPalette[allActivityTypes.indexOf(type) % colorPalette.length];

                    const newChart = new Chart(ctx, {
                        type: 'line',
                        data: {
                            labels,
                            datasets: [{
                                label: type,
                                data,
                                borderColor: color,
                                backgroundColor: `${color}4D`, // 30% opacity
                                fill: true,
                                tension: 0.4,
                            }],
                        },
                        options: {
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                                y: {
                                    beginAtZero: true,
                                    stacked: true,
                                },
                                x: {
                                    stacked: true,
                                }
                            },
                            plugins: {
                                legend: {
                                    display: true,
                                },
                            },
                        },
                    });
                    chartInstances.current.push(newChart);
                }
            });
        }
    }, [logs, allActivityTypes]);

    return (
        <Card className="shadow-sm log-dashboard-card">
            <Card.Header>Activity Log Dashboard</Card.Header>
            <Card.Body>
                <Form>
                    <Row className="mb-3 g-3">
                        <Col md={3}>
                            <Form.Group className="form-floating" controlId="startDate">
                                <DatePicker
                                    selected={startDate}
                                    onChange={date => setStartDate(date)}
                                    className="form-control"
                                    customInput={<CustomDatePickerInput placeholder="Start Date" />}
                                />
                                <Form.Label>Start Date</Form.Label>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="form-floating" controlId="endDate">
                                <DatePicker
                                    selected={endDate}
                                    onChange={date => setEndDate(date)}
                                    className="form-control"
                                    customInput={<CustomDatePickerInput placeholder="End Date" />}
                                />
                                <Form.Label>End Date</Form.Label>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="form-floating" controlId="groupBy">
                                <Form.Select value={groupBy} onChange={e => setGroupBy(e.target.value)}>
                                    <option value="hour">Hour</option>
                                    <option value="day">Day</option>
                                    <option value="week">Week</option>
                                    <option value="month">Month</option>
                                    <option value="quarter">Quarter</option>
                                    <option value="year">Year</option>
                                </Form.Select>
                                <Form.Label>Group By</Form.Label>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="form-floating" controlId="selectedProfileId">
                                <Form.Select value={selectedProfileId} onChange={e => setSelectedProfileId(e.target.value)}>
                                    <option value="">All Profiles</option>
                                    {allProfiles.map(profile => (
                                        <option key={profile.id} value={profile.id}>{profile.name}</option>
                                    ))}
                                </Form.Select>
                                <Form.Label>Profile</Form.Label>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="form-floating" controlId="selectedUserId">
                                <Form.Select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}>
                                    <option value="">All Users</option>
                                    {allUsers.map(user => (
                                        <option key={user.id} value={user.id}>{user.email}</option>
                                    ))}
                                </Form.Select>
                                <Form.Label>User</Form.Label>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="form-floating" controlId="selectedActivityType">
                                <Form.Select value={selectedActivityType} onChange={e => setSelectedActivityType(e.target.value)}>
                                    <option value="">All Activity Types</option>
                                    {allActivityTypes.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </Form.Select>
                                <Form.Label>Activity Type</Form.Label>
                            </Form.Group>
                        </Col>
                        <Col md={3} className="d-flex align-items-end">
                            <Button variant="primary" onClick={fetchLogs} className="w-100">Apply Filters</Button>
                        </Col>
                    </Row>
                </Form>

                <h4 className="mt-4">Aggregated Logs</h4>
                <Row>
                    {allActivityTypes.map(type => (
                        <Col md={6} key={type} className="mb-4">
                            <Card className="log-dashboard-chart-card h-100">
                                <Card.Header>{type} Activity Over Time</Card.Header>
                                <Card.Body>
                                    <div style={{ height: '300px' }}>
                                        <canvas id={`chart-${type}`}></canvas>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Card.Body>
        </Card>
    );
};

export default LogDashboard;